import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';

const WIZARD_STEPS = [
  { key: 'welcome',        label: 'Welcome',          phase: 1, required: true  },
  { key: 'profile',        label: 'Your Information', phase: 1, required: true  },
  { key: 'agent_setup',    label: 'Install Agent',    phase: 1, required: true  },
  { key: 'agent_verify',   label: 'Verify Agent',     phase: 1, required: true  },
  { key: 'network_config', label: 'Network Settings', phase: 2, required: true  },
  { key: 'discovery_scan', label: 'Discover Devices', phase: 2, required: true  },
  { key: 'monitoring',     label: 'Setup Monitoring', phase: 2, required: false },
  { key: 'integrations',   label: 'Integrations',     phase: 3, required: false },
  { key: 'integration_config', label: 'Configure',    phase: 3, required: false, dynamic: true },
  { key: 'recording_assurance', label: 'Recording',   phase: 4, required: false, conditional: true },
  { key: 'completion',     label: 'All Done',         phase: 4, required: true  },
];

const useSiteSetupStore = create((set, get) => ({
  // State
  isWizardOpen: false,
  status: null,
  currentStepIndex: 0,
  completedSteps: [],
  savedData: {},
  prerequisites: {},
  tenant: null,
  loading: true,
  onboardingRequired: false,
  reason: null,

  steps: WIZARD_STEPS,

  // Get current step object
  getCurrentStep: () => WIZARD_STEPS[get().currentStepIndex] || WIZARD_STEPS[0],

  // Actions
  openWizard: () => set({ isWizardOpen: true }),
  closeWizard: () => set({ isWizardOpen: false }),

  // Load status from backend
  loadStatus: async () => {
    try {
      set({ loading: true });
      const res = await fetchWithTenant('/api/onboarding/status');
      if (!res.ok) {
        set({ loading: false, onboardingRequired: false });
        return;
      }
      const data = await res.json();

      // Find the step index to resume at
      let resumeIndex = 0;
      if (data.currentStep) {
        const idx = WIZARD_STEPS.findIndex(s => s.key === data.currentStep);
        if (idx >= 0) {
          // Go to the NEXT step after the last completed one
          resumeIndex = Math.min(idx + 1, WIZARD_STEPS.length - 1);
        }
      }

      set({
        status: data.status,
        currentStepIndex: resumeIndex,
        completedSteps: data.completedSteps || [],
        savedData: data.savedData || {},
        prerequisites: data.prerequisites || {},
        tenant: data.tenant || null,
        onboardingRequired: data.onboardingRequired,
        reason: data.reason,
        loading: false
      });

      // Auto-open if required
      if (data.onboardingRequired) {
        set({ isWizardOpen: true });
      }
    } catch (err) {
      console.error('[SiteSetup] Failed to load status:', err);
      set({ loading: false, onboardingRequired: false });
    }
  },

  // Navigate
  goToNextStep: () => {
    const { currentStepIndex } = get();
    if (currentStepIndex < WIZARD_STEPS.length - 1) {
      set({ currentStepIndex: currentStepIndex + 1 });
    }
  },

  goToPrevStep: () => {
    const { currentStepIndex } = get();
    if (currentStepIndex > 0) {
      set({ currentStepIndex: currentStepIndex - 1 });
    }
  },

  goToStep: (key) => {
    const idx = WIZARD_STEPS.findIndex(s => s.key === key);
    if (idx >= 0) set({ currentStepIndex: idx });
  },

  // Mark step complete + save data
  markStepComplete: async (stepKey, data = {}) => {
    try {
      const res = await fetchWithTenant('/api/onboarding/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: stepKey, data })
      });
      if (res.ok) {
        const result = await res.json();
        set(state => ({
          completedSteps: [...new Set([...state.completedSteps, stepKey])],
          savedData: { ...state.savedData, ...data },
          status: 'IN_PROGRESS'
        }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('[SiteSetup] Failed to save progress:', err);
      return false;
    }
  },

  // Skip wizard
  skipWizard: async (snoozeDays = 7) => {
    try {
      const res = await fetchWithTenant('/api/onboarding/skip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ snoozeDays })
      });
      if (res.ok) {
        set({ isWizardOpen: false, onboardingRequired: false, status: 'SKIPPED' });
        return true;
      }
      return false;
    } catch (err) {
      console.error('[SiteSetup] Failed to skip:', err);
      return false;
    }
  },

  // Complete wizard
  completeWizard: async () => {
    try {
      const res = await fetchWithTenant('/api/onboarding/complete', {
        method: 'POST'
      });
      if (res.ok) {
        set({ isWizardOpen: false, onboardingRequired: false, status: 'COMPLETED' });
        return true;
      }
      return false;
    } catch (err) {
      console.error('[SiteSetup] Failed to complete:', err);
      return false;
    }
  },

  // Reset wizard (re-run from settings)
  resetWizard: async () => {
    try {
      const res = await fetchWithTenant('/api/onboarding/reset', {
        method: 'POST'
      });
      if (res.ok) {
        set({
          status: 'PENDING',
          currentStepIndex: 0,
          completedSteps: [],
          savedData: {},
          onboardingRequired: true,
          isWizardOpen: true
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('[SiteSetup] Failed to reset:', err);
      return false;
    }
  },

  // Check if a step is completed
  isStepComplete: (key) => get().completedSteps.includes(key),
}));

export default useSiteSetupStore;
